// External
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { func, object, string } from 'prop-types';
import { useFormikContext } from 'formik';
import { Box } from 'rebass';
import { TextFieldStyled } from '@nike/nike-design-system-components';
import styled from '@emotion/styled';

// Components
import TextWithBoldSubstr from '../common/TextWithBoldSubstr';

// Functions
import { getCatalogTypesData } from './selectors';
import { ERROR_MESSAGES } from '../catalogs/constants';

import { SpecializedDropdown } from '../../styles/styles';

export const SpecializedCatalogTypeBadge = styled.div`
  background-color: #e5e5e5;
  border-radius: 4px;
  width: 350px;
  padding: 6px 12px;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: 10px;
`;

const CatalogTypeField = ({ associateCatalogType, catalogTypesData, disassociateCatalogType, parentType }) => {
  const { errors, setFieldValue, touched, values } = useFormikContext();

  const [selections, setSelections] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [subString, setSubstring] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const salesOrgRef = useRef(values.salesOrg);
  const validateCatalogTypeDisplay = parentType === 'catalog' && selections.length;
  const formSuggestions = () =>
    Object.values(catalogTypesData)
      .filter(type => !values.catalogTypes.includes(type.id) && type.salesOrg === values.salesOrg)
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

  const handleClick = event => {
    if (!event.target.closest('#catalogTypeField') && !event.target.closest('li[role*=menuitem]')) {
      setShowSuggestions(false);
    }
  };

  const processChange = event => {
    const { value } = event.target;
    const updatedValue = value.toUpperCase();

    // re-establish catalog type data
    const formedSuggestions = formSuggestions();
    const newSuggestions = Object.values(formedSuggestions).filter(({ name }) =>
      name.toUpperCase().includes(updatedValue)
    );

    setSuggestions(newSuggestions.length ? newSuggestions : formedSuggestions);
    setSubstring(newSuggestions.length ? updatedValue : '');
    setShowSuggestions(true);

    return event;
  };

  const handleOnFocus = () => {
    setShowSuggestions(true);
  };

  useEffect(() => {
    setSuggestions(formSuggestions);
    setSelections(
      values.catalogTypes
        .map(id => catalogTypesData[id])
        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
    );
  }, [catalogTypesData, values.catalogTypes]);

  useEffect(() => {
    if (salesOrgRef.current !== values.salesOrg || !salesOrgRef.current) {
      setSelections([]);
      setFieldValue('catalogTypes', []);
      setSuggestions(formSuggestions);
    } else {
      salesOrgRef.current = values.salesOrg;
    }
  }, [values.salesOrg]);

  useEffect(() => {
    document.body.addEventListener('click', handleClick);

    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <Box id='catalogTypeField' sx={{ position: 'relative' }}>
      <TextFieldStyled
        id='catalogTypes'
        data-testid='catalogTypes'
        autoComplete='off'
        error={touched.catalogTypes && errors.catalogTypes}
        errorMessage={ERROR_MESSAGES.catalogType}
        label='Select Type'
        name='catalogTypes'
        onChange={processChange}
        onFocus={validateCatalogTypeDisplay ? () => {} : handleOnFocus}
        readOnly={!values.salesOrg || validateCatalogTypeDisplay}
        inputProps={{ 'data-testid': 'catalogTypes' }}
      />
      {showSuggestions && suggestions.length > 0 && (
        <SpecializedDropdown role='menu'>
          {suggestions.map(({ id, name }) => (
            <li key={id} role='menuitem' aria-label='catalog-menu-item'>
              <div
                key={id}
                onClick={() => {
                  associateCatalogType(setFieldValue)(id)(values);
                  if (parentType === 'catalog') {
                    setShowSuggestions(false);
                  }
                }}
              >
                <TextWithBoldSubstr str={name} substr={subString} />
              </div>
            </li>
          ))}
        </SpecializedDropdown>
      )}

      {selections.length > 0 && (
        <section>
          {selections.map(({ id, name }) => (
            <SpecializedCatalogTypeBadge
              key={id}
              onClick={() => disassociateCatalogType(setFieldValue)(id)(values)}
              role='listitem'
            >
              {name}
            </SpecializedCatalogTypeBadge>
          ))}
        </section>
      )}
    </Box>
  );
};

CatalogTypeField.defaultProps = {
  associateCatalogType: null,
  catalogTypesData: {},
  disassociateCatalogType: null,
  parentType: ''
};

CatalogTypeField.propTypes = {
  associateCatalogType: func,
  catalogTypesData: object,
  disassociateCatalogType: func,
  parentType: string
};

const mapStateToProps = state => ({
  catalogTypesData: getCatalogTypesData(state)
});

export default connect(mapStateToProps)(CatalogTypeField);
